import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { PostingFrequency } from './SocialFrequencyDisplay';

interface Platform {
  id: string;
  name: string;
  icon: string;
}

interface AddSocialNetworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (platform: string, frequency: PostingFrequency) => void;
  existingPlatforms?: string[];
}

const platforms: Platform[] = [
  { id: 'linkedin', name: 'LinkedIn', icon: '/linkedin.svg' },
  { id: 'twitter', name: 'Twitter', icon: '/twitter.svg' },
  { id: 'instagram', name: 'Instagram', icon: '/instagram.svg' },
  { id: 'facebook', name: 'Facebook', icon: '/facebook.svg' },
  { id: 'youtube', name: 'YouTube', icon: '/youtube.svg' },
  { id: 'tiktok', name: 'TikTok', icon: '/tiktok.svg' },
  { id: 'pinterest', name: 'Pinterest', icon: '/pinterest.svg' },
  { id: 'google-business', name: 'Google Business', icon: '/google-business.svg' }
];

const frequencies: PostingFrequency[] = ['daily', 'weekly', 'bi-weekly', 'monthly'];

export const AddSocialNetworkModal: React.FC<AddSocialNetworkModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  existingPlatforms = []
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [selectedFrequency, setSelectedFrequency] = useState<PostingFrequency | ''>('');

  // Reset selections when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedPlatform('');
      setSelectedFrequency('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePlatformClick = (platformId: string, isDisabled: boolean) => {
    if (isDisabled) return;
    
    if (selectedPlatform === platformId) {
      setSelectedPlatform('');
    } else {
      setSelectedPlatform(platformId);
    }
  };

  const handleFrequencyClick = (frequency: PostingFrequency) => {
    if (selectedFrequency === frequency) {
      setSelectedFrequency('');
    } else {
      setSelectedFrequency(frequency);
    }
  };

  const handleAdd = () => {
    if (selectedPlatform && selectedFrequency) {
      onAdd(selectedPlatform, selectedFrequency);
      onClose();
    }
  };

  const getFrequencyLabel = (frequency: PostingFrequency) => {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'bi-weekly':
        return 'Twice per week';
      case 'monthly':
        return 'Monthly';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Add Social Network</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Select Platform
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {platforms.map(platform => {
                  const isDisabled = existingPlatforms.includes(platform.id);
                  return (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformClick(platform.id, isDisabled)}
                      disabled={isDisabled}
                      className={`flex items-center gap-2 p-3 rounded-lg border ${
                        selectedPlatform === platform.id
                          ? 'border-primary-600 bg-primary-50'
                          : isDisabled
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-primary-600'
                      }`}
                    >
                      <img src={platform.icon} alt={platform.name} className="w-6 h-6" />
                      <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                        {platform.name}
                      </span>
                      {isDisabled && (
                        <span className="text-xs text-gray-400 ml-auto">
                          Already added
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Posting Frequency
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {frequencies.map(frequency => (
                  <button
                    key={frequency}
                    onClick={() => handleFrequencyClick(frequency)}
                    className={`p-3 rounded-lg border ${
                      selectedFrequency === frequency
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-600'
                    }`}
                  >
                    <span className="text-sm text-gray-700">
                      {getFrequencyLabel(frequency)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!selectedPlatform || !selectedFrequency}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Network
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};